import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Spin, Button, Collapse } from 'antd';
import { CheckOutlined, LoginOutlined, DesktopOutlined, MobileOutlined, AndroidFilled, AppleFilled } from '@ant-design/icons';
import { PaymentAPI } from '../backend/api';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

const { Paragraph, Text } = Typography;

const extractData = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (d?.data && Array.isArray(d.data)) return d.data;
  return [];
};

const HomePage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchPlans = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const res = await PaymentAPI.getPlans();
      const data = extractData(res);
      if (Array.isArray(data) && data.length > 0) {
        setPlans(data);
      } else {
        // Fallback plans if API returns empty
        setPlans([
          {
            name: 'monthly',
            displayName: 'Monthly',
            price: 399,
            currency: 'SEK',
            period: 'Monthly',
            description: 'Full access for one month',
          },
          {
            name: 'yearly',
            displayName: 'Yearly',
            price: 999,
            currency: 'SEK',
            period: 'Yearly',
            description: 'Full access for 12 months',
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setHasError(true);
      // Fallback plans on error
      setPlans([
        {
          name: 'monthly',
          displayName: 'Monthly',
          price: 399,
          currency: 'SEK',
          period: 'Monthly',
          description: 'Full access for one month',
        },
        {
          name: 'yearly',
          displayName: 'Yearly',
          price: 999,
          currency: 'SEK',
          period: 'Yearly',
          description: 'Full access for 12 months',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handlePlanClick = (plan) => {
    if (!user) {
      localStorage.setItem('pendingPlan', JSON.stringify({
        planName: plan.name,
        pricingName: 'pricing_bingeit',
        productName: plan.displayName,
        price: plan.price,
        currency: plan.currency || 'SEK',
      }));
      login();
      return;
    }
    navigate('/payment');
  };

  const renderPlanCard = (plan) => {
    const isYearly = plan.period === 'Yearly';
    return (
      <Col key={plan.name} xs={24} md={12}>
        <Card
          className={`plan-card${isYearly ? ' best-value' : ''}`}
          style={{
            textAlign: 'center',
            border: isYearly ? '2px solid #1890ff' : '1px solid #303030',
            background: isYearly ? 'rgba(24,144,255,0.08)' : '#141414',
          }}
        >
          {isYearly && (
            <div className="plan-badge" style={{ color: '#1890ff', fontSize: 14, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
              BEST VALUE
            </div>
          )}
          <div className="plan-name" style={{ color: '#fff', fontSize: 24, marginTop: 8 }}>{plan.displayName}</div>
          <div className="price" style={{ color: '#1890ff', fontSize: 48, fontWeight: 'bold', margin: '16px 0' }}>
            {plan.price}:-
            <span className="price-period" style={{ color: 'rgba(255,255,255,0.65)', fontSize: 16, fontWeight: 'normal' }}>
              {isYearly ? ' /12 months' : '/month'}
            </span>
          </div>
          <Paragraph style={{ color: 'rgba(255,255,255,0.65)' }}>
            {plan.description}
          </Paragraph>
          <ul className="feature-list" style={{ listStyle: 'none', padding: 0, textAlign: 'left', marginBottom: 24 }}>
            <li style={{ padding: '6px 0', color: 'rgba(255,255,255,0.65)', fontSize: 14 }}>
              <CheckOutlined style={{ color: '#52c41a', marginRight: 8 }} />Full access to all movies & shows
            </li>
            <li style={{ padding: '6px 0', color: 'rgba(255,255,255,0.65)', fontSize: 14 }}>
              <CheckOutlined style={{ color: '#52c41a', marginRight: 8 }} />4K streaming quality
            </li>
            <li style={{ padding: '6px 0', color: 'rgba(255,255,255,0.65)', fontSize: 14 }}>
              <CheckOutlined style={{ color: '#52c41a', marginRight: 8 }} />Cancel anytime
            </li>
          </ul>
          <Button
            type={isYearly ? 'primary' : 'default'}
            size="large"
            block
            onClick={() => handlePlanClick(plan)}
          >
            {user ? 'Subscribe Now' : 'Create Account & Pay'}
          </Button>
        </Card>
      </Col>
    );
  };

  return (
    <div>
      {/* ===== HERO SECTION ===== */}
      <div className="landing-hero">
        <h1>BingeIt.VIP</h1>
        <p className="hero-tagline">Stream Movies & TV Shows. Anytime. Anywhere.</p>
        <p className="hero-description">
          Your premium community media streaming service. Create an account, choose a plan,
          and start watching on all your devices.
        </p>
        <div className="hero-cta">
          <Button type="primary" size="large" onClick={() => login()}>
            <LoginOutlined /> Create Account & Subscribe
          </Button>
          <Button ghost size="large" onClick={() => login()}>
            Sign In
          </Button>
        </div>
        {!user && (
          <div style={{ marginTop: 24 }}>
            <Text style={{ color: 'rgba(255,255,255,0.7)' }}>
              Already a customer?
            </Text>
            <Button
              type="link"
              style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}
              onClick={() => login()}
            >
              Log in
            </Button>
          </div>
        )}
      </div>

      {/* ===== SUBSCRIPTION PLANS SECTION ===== */}
      <div className="plans-section">
        <h2 className="section-title">Choose Your Plan</h2>
        <p className="section-subtitle">
          Pick the plan that fits you best. Cancel anytime, no strings attached.
        </p>

        {isLoading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : hasError && plans.length === 0 ? (
          <div className="error-container">
            <p className="error-message">Plans temporarily unavailable. Please try again later.</p>
            <Button type="primary" onClick={fetchPlans}>Retry</Button>
          </div>
        ) : (
          <Row gutter={[24, 24]} style={{ maxWidth: 800, margin: '0 auto' }}>
            {plans.map(renderPlanCard)}
          </Row>
        )}
      </div>

      {/* ===== DEVICE SETUP GUIDE ===== */}
      <div className="device-guide-section">
        <h2 className="section-title">Watch on Your Devices</h2>
        <p className="section-subtitle">
          Download Jellyfin on your favorite device and connect to BingEit. It's free and easy.
        </p>

        <Row gutter={[16, 16]}>
          {[
            { name: 'Android TV', icon: <DesktopOutlined className="device-icon" />, store: 'Google Play Store', note: '' },
            { name: 'Apple TV', icon: <AppleFilled className="device-icon" />, store: 'App Store', note: '' },
            { name: 'iPhone & iPad', icon: <MobileOutlined className="device-icon" />, store: 'App Store', note: '' },
            { name: 'Android Phone', icon: <AndroidFilled className="device-icon" />, store: 'Google Play Store', note: '' },
            { name: 'Amazon Fire TV', icon: <DesktopOutlined className="device-icon" />, store: 'Amazon Appstore', note: '' },
            { name: 'Samsung TV', icon: <DesktopOutlined className="device-icon" />, store: 'Samsung App Store', note: '' },
            { name: 'LG TV', icon: <DesktopOutlined className="device-icon" />, store: 'LG Content Store', note: '' },
            { name: 'Web Browser', icon: <DesktopOutlined className="device-icon" />, store: '', note: 'no-download' },
          ].map((device) => (
            <Col key={device.name} xs={24} sm={12} md={8} lg={6}>
              <Card className="device-card" style={{ height: '100%', background: '#141414', border: '1px solid #303030', borderRadius: 12 }}>
                <div style={{ fontSize: 48, marginBottom: 12, textAlign: 'center', color: '#1890ff' }}>
                  {device.icon}
                </div>
                <div className="device-name" style={{ color: '#fff', fontSize: 18, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>
                  {device.name}
                </div>
                {device.note === 'no-download' ? (
                  <p className="no-download" style={{ color: '#52c41a', fontWeight: 600, textAlign: 'center', marginTop: 12 }}>
                    No download needed!<br />
                    Visit <strong>watch.bingeit.vip</strong><br />
                    and log in with your BingEit account.
                  </p>
                ) : (
                  <ol className="step-list" style={{ listStyle: 'none', padding: 0, textAlign: 'left' }}>
                    {[
                      `Download Jellyfin from the ${device.store}`,
                      'Open the app on your device',
                      'Enter server address: watch.bingeit.vip',
                      'Log in with your BingEit account',
                      'Start streaming!',
                    ].map((step, i) => (
                      <li key={i} style={{ padding: '6px 0', color: 'rgba(255,255,255,0.65)', fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span className="step-number" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 22, borderRadius: '50%', background: '#1890ff', color: 'white', fontSize: 12, fontWeight: 'bold', flexShrink: 0, marginTop: 1 }}>
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* ===== FAQ + FOOTER ===== */}
      <div className="faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <Collapse
          accordion
          expandIconPosition="end"
          items={[
            {
              key: '1',
              label: 'What is BingEit?',
              children: 'BingEit is a premium community media streaming service that gives you access to thousands of movies and TV shows. Create an account, subscribe, and start watching on any device.',
            },
            {
              key: '2',
              label: 'How do I create an account?',
              children: 'Click "Create Account & Subscribe" on the homepage. You will be guided through our secure sign-up process via Casdoor. Once your account is created, you can choose a subscription plan.',
            },
            {
              key: '3',
              label: 'What subscription plans are available?',
              children: 'We offer two plans: Monthly at 399 SEK/month and Yearly at 999 SEK for 12 months. The yearly plan saves you money compared to the monthly rate. Both plans include full access to our entire library, 4K streaming, and the ability to cancel anytime.',
            },
            {
              key: '4',
              label: 'How do I watch on my TV?',
              children: 'Download the Jellyfin app from your TV\'s app store (available on Android TV, Apple TV, Samsung TV, LG TV, and Amazon Fire TV). Open the app, enter the server address watch.bingeit.vip, and log in with your BingEit account.',
            },
            {
              key: '5',
              label: 'Can I cancel my subscription?',
              children: 'Yes, you can cancel your subscription at any time. There are no long-term contracts or commitments. Your access will continue until the end of your current billing period.',
            },
            {
              key: '6',
              label: 'What devices are supported?',
              children: 'BingEit works on Android TV, Apple TV, iPhone & iPad, Android phones & tablets, Amazon Fire TV, Samsung TV, LG TV, and any web browser. Simply download the Jellyfin app from your device\'s app store and connect to watch.bingeit.vip.',
            },
            {
              key: '7',
              label: 'How do I request a movie?',
              children: 'Go to the Requests page from the navigation menu. You can search for movies and submit requests. Other community members can upvote requests, and popular requests get added to our library.',
            },
          ]}
        />
      </div>

      {/* ===== FOOTER ===== */}
      <div className="site-footer">
        <div className="footer-brand">BingEit.VIP</div>
        <div className="footer-links">
          <a href="/">Home</a>
          <a href="/payment">Subscription</a>
          <a href="/requests">Requests</a>
        </div>
        <div className="footer-copyright">© 2026 BingEit.VIP. All rights reserved.</div>
        <div className="footer-credits">Powered by Jellyfin &amp; Casdoor</div>
      </div>
    </div>
  );
};

export default HomePage;
